import React, { useEffect } from "react";
import { Typography, Slider } from "@mui/material";
import { tss } from "tss-react/mui";
import { SkipNext, SkipPrevious } from "@mui/icons-material";

var useStyles = tss.create(({theme}) => ({
    root: {
        color: "#283C50",
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
    },
    right: {
        position: "absolute",
        display: "inline-block",
        right: 5,
    },
    title: {
        fontSize: 14,
    },
    sliderInput: {
        display: "inline-block",
        width: 70,
        outline: 0,
        border: 0,
        textAlign: "right",
        backgroundColor: "transparent",
        color: theme.palette.text.secondary,
        borderBottom: `solid 1px ${theme.palette.divider}`,
    },
    icon: {
        position: "relative",
        width: 16,
        height: 16,
        verticalAlign: "middle",
        display: "inline-block",
        top: 2,
        cursor: "pointer",
    },
    label: {
        position: "relative",
        left: 2,
        display: "inline-block",
    },
    content: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 35,
        width: "100%",
    },
    slider: {
        width: "85%",
        textAlign: "center",
        height: 24,
    },
    arrowLeft: {
        marginRight: 5,
    },
    arrowRight: {
        marginLeft: 5,
    },
}));

var globalSettings: any = [];

const timeout_freq = 200;
const round = (num: number) => {
    return Math.round((num + Number.EPSILON) * 100) / 100;
};

type IProps = {
    title: string;
    name: string;
    min: number;
    max: number;
    value: number;
    step: number;
    id?: string;
    icon?: any;
    style?: any;
    className?: any;
    handleChange: (evt: any, propertyName: string, value: any) => void;
};

type IState = {
    update: boolean;
};

function SliderWithInput(props: IProps) {
    const { classes } = useStyles();
    var { id, title, name, min, max, step, icon, style, className, handleChange } = props;
    const [update, setUpdate] = React.useState(false);

    let inputElem: any;
    let intervalId: any;

    if (!id) {
        id = "slider";
    }

    if (typeof globalSettings[name + "-" + id] === "undefined") {
        globalSettings[name + "-" + id] = props.value;
    }
    //console.log('globalSettings:', globalSettings);
    useEffect(() => {
        globalSettings[name + "-" + id] = props.value;
        setUpdate((prevUpate) => !prevUpate);
    }, [id, name, props.value]);

    var value = globalSettings[name + "-" + id];

    const handleSliderChange = (evt: any, newValue: any) => {
        newValue = round(parseFloat(newValue));
        globalSettings[name + "-" + id] = newValue;
        handleChange(evt, name, newValue);
        setUpdate((prevUpate) => !prevUpate);
    };

    const handleInputChange = (evt: any) => {
        let newValue = round(parseFloat(evt.target.value));
        globalSettings[name + "-" + id] = newValue;
        handleChange(evt, name, newValue);
        setUpdate((prevUpate) => !prevUpate);
    };

    const stopInterval = (evt: any) => {
        clearTimeout(intervalId);
    };

    const increaseValue = (evt: any) => {
        if (inputElem) {
            let value = round(parseFloat(inputElem.value));
            value = value + step;
            if (value <= max) {
                inputElem.stepUp();
                handleChange(evt, name, value);
                globalSettings[name + "-" + id] = value;
                setUpdate((prevUpate) => !prevUpate);
                stopInterval(evt);
                intervalId = setTimeout(increaseValue, timeout_freq, evt);
            }
        }
    };

    const decreaseValue = (evt: any) => {
        if (inputElem) {
            let value = round(parseFloat(inputElem.value));
            value = value - step;
            if (value >= min) {
                inputElem.stepDown();
                handleChange(evt, name, value);
                globalSettings[name + "-" + id] = value;
                setUpdate((prevUpate) => !prevUpate);
                stopInterval(evt);
                intervalId = setTimeout(decreaseValue, timeout_freq, evt);
            }
        }
    };

    return (
        <div style={{ ...style }} className={[classes.root, className].join(" ")}>
            <div style={{ position: "relative", userSelect: update ? "none" : "none" }}>
                {icon ? icon : ""}
                <div className={classes.label}>
                    <Typography className={classes.title}>{title}</Typography>
                </div>
                <div className={classes.right}>
                    <input
                        ref={(elem: any) => (inputElem = elem)}
                        name={"input-" + name}
                        type="number"
                        min={min}
                        max={max}
                        step={step}
                        value={round(value)}
                        className={classes.sliderInput}
                        onChange={handleInputChange}
                    />
                </div>
            </div>
            <div className={classes.content}>
                <div className={classes.arrowLeft}>
                    <SkipPrevious
                        className={classes.icon}
                        onMouseDown={decreaseValue}
                        onMouseUp={stopInterval}
                        onMouseOut={stopInterval}
                    />
                </div>
                <div className={classes.slider}>
                    <Slider
                        key={`slider-${name}`}
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={handleSliderChange}
                        aria-labelledby="continuous-slider"
                    />
                </div>
                <div className={classes.arrowRight}>
                    <SkipNext
                        className={classes.icon}
                        onMouseDown={increaseValue}
                        onMouseUp={stopInterval}
                        onMouseOut={stopInterval}
                    />
                </div>
            </div>
        </div>
    );
}

export default class SliderInput extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            update: false,
        };
    }

    updateSlider(value: number) {
        if (typeof globalSettings[this.props.name + "-" + this.props.id] !== "undefined") {
            globalSettings[this.props.name + "-" + this.props.id] = value;
        }
        this.setState({ update: !this.state.update });
    }

    render() {
        return <SliderWithInput {...this.props} />;
    }
}
